import { useLaborCostDashboardStore } from 'pages/ReportsPage/components/AnalyticsDashboards/LaborCostDashboard/store/laborCostDashboard.store';
import { useAnalyticsFilterStore } from 'pages/ReportsPage/store/analyticsFilterView.context';
import { KpiStatCardProps } from 'pages/ReportsPage/components/AnalyticsDashboards/KpiStatCard';
import { formatDashboardNumber } from 'pages/ReportsPage/components/AnalyticsDashboards/formatDashboardNumber';
import {
  buildClearedCard,
  buildErrorCard,
  buildValueCard,
} from 'pages/ReportsPage/components/AnalyticsDashboards/kpiCardBuilders';
import { isSelectionCleared } from 'pages/ReportsPage/store/analyticsSelection.utils';

function getComparisonText(dateRange: [string, string]): string {
  const start = new Date(dateRange[0]);
  const end = new Date(dateRange[1]);
  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  const days = Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
  return `vs. prior ${days} days`;
}

export function useLaborCostKpiCards(): KpiStatCardProps[] {
  const locationIds = useAnalyticsFilterStore((s) => s.locationIds);
  const positions = useAnalyticsFilterStore((s) => s.positions);
  const calloutHours = useLaborCostDashboardStore((s) => s.calloutHours);
  const calloutHoursPeriodDays = useLaborCostDashboardStore((s) => s.calloutHoursPeriodDays);
  const calloutHoursChangePrct = useLaborCostDashboardStore((s) => s.calloutHoursChangePrct);
  const calloutHoursError = useLaborCostDashboardStore((s) => s.calloutHoursError);
  const dailyOvertimeHours = useLaborCostDashboardStore((s) => s.dailyOvertimeHours);
  const dailyOvertimeHoursPeriodDays = useLaborCostDashboardStore(
    (s) => s.dailyOvertimeHoursPeriodDays,
  );
  const dailyOvertimeHoursChangePrct = useLaborCostDashboardStore(
    (s) => s.dailyOvertimeHoursChangePrct,
  );
  const dailyOvertimeHoursError = useLaborCostDashboardStore((s) => s.dailyOvertimeHoursError);
  const unfulfilledHours = useLaborCostDashboardStore((s) => s.unfulfilledHours);
  const unfulfilledHoursPeriodDays = useLaborCostDashboardStore(
    (s) => s.unfulfilledHoursPeriodDays,
  );
  const unfulfilledHoursChangePrct = useLaborCostDashboardStore(
    (s) => s.unfulfilledHoursChangePrct,
  );
  const unfulfilledHoursError = useLaborCostDashboardStore((s) => s.unfulfilledHoursError);
  const scheduledHours = useLaborCostDashboardStore((s) => s.scheduledHours);
  const scheduledHoursPeriodDays = useLaborCostDashboardStore((s) => s.scheduledHoursPeriodDays);
  const scheduledHoursChangePrct = useLaborCostDashboardStore((s) => s.scheduledHoursChangePrct);
  const scheduledHoursError = useLaborCostDashboardStore((s) => s.scheduledHoursError);
  const dateRange = useAnalyticsFilterStore((s) => s.dateRange);

  const fallbackComparisonText = getComparisonText(dateRange);
  const comparisonTextFor = (periodDays: number | null): string =>
    periodDays != null ? `vs. prior ${periodDays} days` : fallbackComparisonText;

  const calloutHoursCard = calloutHoursError
    ? buildErrorCard('Call Out Hours')
    : buildValueCard(
        'Call Out Hours',
        formatDashboardNumber(calloutHours),
        calloutHoursChangePrct,
        comparisonTextFor(calloutHoursPeriodDays),
      );

  const dailyOvertimeHoursCard = dailyOvertimeHoursError
    ? buildErrorCard('Daily Overtime Hours')
    : buildValueCard(
        'Daily Overtime Hours',
        formatDashboardNumber(dailyOvertimeHours),
        dailyOvertimeHoursChangePrct,
        comparisonTextFor(dailyOvertimeHoursPeriodDays),
      );

  const unfulfilledHoursCard = unfulfilledHoursError
    ? buildErrorCard('Unfulfilled Hours')
    : buildValueCard(
        'Unfulfilled Hours',
        formatDashboardNumber(unfulfilledHours),
        unfulfilledHoursChangePrct,
        comparisonTextFor(unfulfilledHoursPeriodDays),
      );

  const scheduledHoursCard = scheduledHoursError
    ? buildErrorCard('Scheduled Hours')
    : buildValueCard(
        'Scheduled Hours',
        formatDashboardNumber(scheduledHours),
        scheduledHoursChangePrct,
        comparisonTextFor(scheduledHoursPeriodDays),
      );

  const cards: KpiStatCardProps[] = [
    calloutHoursCard,
    dailyOvertimeHoursCard,
    unfulfilledHoursCard,
    scheduledHoursCard,
  ];

  return isSelectionCleared(locationIds, positions)
    ? cards.map((card) => buildClearedCard(card.label))
    : cards;
}
